import React, { useEffect, useState, useMemo } from "react";
import { api, formatCurrency, formatApiErrorDetail } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, Receipt } from "lucide-react";
import { toast } from "sonner";

const STATUS_STYLE = {
  SUCCESS: "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300",
  PENDING: "bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-300",
  FAILED: "bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-300",
  CANCELLED: "bg-slate-200 text-slate-600 dark:bg-slate-800 dark:text-slate-400",
  REFUNDED: "bg-blue-100 text-blue-700 dark:bg-blue-500/20 dark:text-blue-300",
};

export default function SuperAdminTransactions() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [providerFilter, setProviderFilter] = useState("all");

  const load = () => api.get("/superadmin/transactions").then((r) => setItems(r.data)).catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));
  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, []);

  const providers = useMemo(() => Array.from(new Set(items.map((t) => t.provider_key).filter(Boolean))), [items]);

  const filtered = items.filter((t) => {
    if (statusFilter !== "all" && t.status !== statusFilter) return false;
    if (providerFilter !== "all" && t.provider_key !== providerFilter) return false;
    if (q && ![t.company_name, t.transaction_ref, t.customer_email, t.plan_id].filter(Boolean).some((v) => v.toLowerCase().includes(q.toLowerCase()))) return false;
    return true;
  });

  const totalSuccess = items.filter((t) => t.status === "SUCCESS").reduce((s, t) => s + Number(t.amount || 0), 0);

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between gap-2">
        <div>
          <h1 className="font-display text-3xl font-bold">Transactions</h1>
          <p className="text-sm text-slate-500">{items.length} transactions · Encaissé : <span className="font-mono font-semibold text-emerald-600">{formatCurrency(totalSuccess, "XOF")}</span></p>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-2">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input placeholder="Rechercher entreprise, référence, email…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" data-testid="sa-txn-search" />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="sm:w-40" data-testid="sa-txn-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous statuts</SelectItem>
            {["SUCCESS", "PENDING", "FAILED", "CANCELLED", "REFUNDED"].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        {providers.length > 0 && (
          <Select value={providerFilter} onValueChange={setProviderFilter}>
            <SelectTrigger className="sm:w-40" data-testid="sa-txn-provider-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous fournisseurs</SelectItem>
              {providers.map(p => <SelectItem key={p} value={p}>{p.toUpperCase()}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
      </div>

      <Card>
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-widest text-slate-500 border-b border-slate-200 dark:border-slate-800">
              <tr>
                <th className="text-left p-3">Référence</th>
                <th className="text-left">Entreprise</th>
                <th className="text-left">Plan</th>
                <th className="text-left">Fournisseur</th>
                <th className="text-right">Montant</th>
                <th className="text-left">Statut</th>
                <th className="text-left">Date</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
              {filtered.map((t) => {
                const cls = STATUS_STYLE[t.status] || STATUS_STYLE.PENDING;
                return (
                  <tr key={t.id} className="hover:bg-slate-50 dark:hover:bg-slate-900/50" data-testid={`sa-txn-row-${t.id}`}>
                    <td className="p-3">
                      <div className="font-mono text-xs">{t.transaction_ref}</div>
                      <div className="text-xs text-slate-500">{t.customer_email || "—"}</div>
                    </td>
                    <td>
                      <div className="font-semibold">{t.company_name || "—"}</div>
                      <div className="text-xs text-slate-500 font-mono">{(t.company_id || "").slice(0, 8)}…</div>
                    </td>
                    <td className="font-semibold">{(t.plan_id || "").toUpperCase()} <span className="text-xs text-slate-500">{t.billing_cycle}</span></td>
                    <td className="text-xs uppercase font-semibold">{t.provider_key}</td>
                    <td className="text-right font-mono">{formatCurrency(t.amount, t.currency)}</td>
                    <td><span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${cls}`}>{t.status}</span></td>
                    <td className="text-xs">
                      {t.paid_at ? <div>Payé : {new Date(t.paid_at).toLocaleString("fr-FR")}</div> : null}
                      <div className="text-slate-500">{new Date(t.created_at).toLocaleString("fr-FR")}</div>
                    </td>
                  </tr>
                );
              })}
              {filtered.length === 0 && (
                <tr><td colSpan={7} className="p-10 text-center text-slate-500">
                  <Receipt className="w-8 h-8 mx-auto text-slate-400 mb-2" />
                  <div>Aucune transaction {q || statusFilter !== "all" ? "correspond aux filtres" : "pour l'instant"}</div>
                </td></tr>
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}
